/**
 * Single shared WebSocket manager.
 *
 * Lifecycle:
 *   1. connect() — fetches a fresh `ws-token`, opens wss to /ws.
 *   2. on open: sends `hello {kind: "ui", session_token, client_version}`.
 *   3. on `welcome`: marks status="open" and flushes queued sends.
 *   4. heartbeats every 25 s; if no pong in 60 s, drop and reconnect.
 *   5. on close/error: reconnects with jittered exponential backoff (cap 15 s).
 *
 * The manager exposes a small subscriber API for the rest of the app.
 * Higher-level concerns (cache invalidation, fleet diffing) live in hooks/.
 *
 * Side effect: a top-level subscriber dispatches agent.* envelopes into the
 * role-keyed chat store and the permission-prompt queue. Components don't
 * need to wire their own listeners for those.
 */

import {
  envelope,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_TIMEOUT_MS,
  type AgentPermissionRequest,
  type AgentPermissionResponse,
  type AgentStream,
  type AgentToolCall,
  type BodyByType,
  type Envelope,
  type MessageType,
} from "@paperclip-cloud/shared";
import { authApi } from "./queries/auth";
import { useChatStore, type ToolCall } from "../stores/chat";
import { usePermissionStore } from "../stores/permissions";

const CLIENT_VERSION = "0.1.0";
const RECONNECT_BASE_MS = 1000;
const RECONNECT_CAP_MS = 15_000;

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed";

type Listener = (env: Envelope) => void;

export interface WsManager {
  connect(): void;
  disconnect(): void;
  send<T extends MessageType>(t: T, body: BodyByType[T], opts?: { id?: string; ref?: string; company_id?: string }): void;
  subscribe(listener: Listener): () => void;
  onStatus(listener: (s: ConnectionStatus) => void): () => void;
  status(): ConnectionStatus;
}

interface InternalState {
  ws: WebSocket | null;
  status: ConnectionStatus;
  reconnectAttempt: number;
  reconnectTimer: number | null;
  heartbeatTimer: number | null;
  heartbeatTimeoutTimer: number | null;
  listeners: Set<Listener>;
  statusListeners: Set<(s: ConnectionStatus) => void>;
  outbox: Envelope[];
  manualClose: boolean;
}

function jitter(ms: number): number {
  return ms * (0.7 + Math.random() * 0.6);
}

function nextBackoff(attempt: number): number {
  const exp = Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** attempt);
  return Math.round(jitter(exp));
}

function defaultUrl(): string {
  const hub = (import.meta.env.VITE_HUB_URL as string | undefined) ?? "";
  if (hub) {
    const u = new URL(hub);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    u.pathname = "/ws";
    return u.toString();
  }
  // Same-origin fallback (the production case where the hub serves the app).
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

export function createWsManager(): WsManager {
  const state: InternalState = {
    ws: null,
    status: "idle",
    reconnectAttempt: 0,
    reconnectTimer: null,
    heartbeatTimer: null,
    heartbeatTimeoutTimer: null,
    listeners: new Set(),
    statusListeners: new Set(),
    outbox: [],
    manualClose: false,
  };

  const setStatus = (s: ConnectionStatus) => {
    if (state.status === s) return;
    state.status = s;
    state.statusListeners.forEach((l) => l(s));
  };

  const clearTimers = () => {
    if (state.heartbeatTimer != null) {
      window.clearInterval(state.heartbeatTimer);
      state.heartbeatTimer = null;
    }
    if (state.heartbeatTimeoutTimer != null) {
      window.clearTimeout(state.heartbeatTimeoutTimer);
      state.heartbeatTimeoutTimer = null;
    }
    if (state.reconnectTimer != null) {
      window.clearTimeout(state.reconnectTimer);
      state.reconnectTimer = null;
    }
  };

  const flushOutbox = () => {
    if (!state.ws || state.ws.readyState !== WebSocket.OPEN) return;
    const queue = state.outbox.splice(0);
    for (const env of queue) state.ws.send(JSON.stringify(env));
  };

  const beginHeartbeat = () => {
    if (state.heartbeatTimer != null) window.clearInterval(state.heartbeatTimer);
    state.heartbeatTimer = window.setInterval(() => {
      if (!state.ws || state.ws.readyState !== WebSocket.OPEN) return;
      state.ws.send(JSON.stringify(envelope("ping", {})));
      if (state.heartbeatTimeoutTimer != null)
        window.clearTimeout(state.heartbeatTimeoutTimer);
      state.heartbeatTimeoutTimer = window.setTimeout(() => {
        // No pong received — kick the socket; the close handler reconnects.
        try {
          state.ws?.close();
        } catch {
          /* noop */
        }
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  };

  const scheduleReconnect = () => {
    if (state.manualClose) return;
    setStatus("reconnecting");
    const delay = nextBackoff(state.reconnectAttempt++);
    state.reconnectTimer = window.setTimeout(() => doConnect(), delay);
  };

  const send: WsManager["send"] = (t, body, opts) => {
    const env = envelope(t, body, opts);
    if (state.ws && state.ws.readyState === WebSocket.OPEN) {
      state.ws.send(JSON.stringify(env));
    } else {
      state.outbox.push(env);
    }
  };

  async function doConnect() {
    if (state.ws && (state.ws.readyState === WebSocket.OPEN || state.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    setStatus(state.reconnectAttempt === 0 ? "connecting" : "reconnecting");

    let token: string;
    try {
      const resp = await authApi.wsToken();
      token = resp.session_token;
    } catch (err) {
      // No session — bail. UI will redirect to /login.
      console.warn("[ws] failed to mint ws-token", err);
      scheduleReconnect();
      return;
    }

    const ws = new WebSocket(defaultUrl());
    state.ws = ws;

    ws.onopen = () => {
      ws.send(
        JSON.stringify(
          envelope("hello", {
            kind: "ui",
            session_token: token,
            client_version: CLIENT_VERSION,
          }),
        ),
      );
    };

    ws.onmessage = (ev) => {
      let env: Envelope;
      try {
        env = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (env.t === "welcome") {
        state.reconnectAttempt = 0;
        setStatus("open");
        beginHeartbeat();
        flushOutbox();
      } else if (env.t === "ping") {
        ws.send(JSON.stringify(envelope("pong", {}, { ref: env.id })));
      } else if (env.t === "pong") {
        if (state.heartbeatTimeoutTimer != null) {
          window.clearTimeout(state.heartbeatTimeoutTimer);
          state.heartbeatTimeoutTimer = null;
        }
      }
      // Always fan out — let listeners handle whatever they care about.
      state.listeners.forEach((l) => l(env));
    };

    ws.onclose = () => {
      clearTimers();
      state.ws = null;
      if (state.manualClose) {
        setStatus("closed");
        return;
      }
      scheduleReconnect();
    };

    ws.onerror = () => {
      // The browser will follow with onclose; nothing to do here.
    };
  }

  return {
    connect() {
      state.manualClose = false;
      doConnect();
    },
    disconnect() {
      state.manualClose = true;
      clearTimers();
      try {
        state.ws?.close();
      } catch {
        /* noop */
      }
      state.ws = null;
      setStatus("closed");
    },
    send,
    subscribe(listener) {
      state.listeners.add(listener);
      return () => state.listeners.delete(listener);
    },
    onStatus(listener) {
      state.statusListeners.add(listener);
      // emit current synchronously so subscribers can render immediately
      listener(state.status);
      return () => state.statusListeners.delete(listener);
    },
    status: () => state.status,
  };
}

// One shared instance for the app.
export const ws = createWsManager();

/* -- Top-level dispatch ---------------------------------------------------- */

/**
 * Wire agent.* envelopes into the role-keyed chat store and the permission
 * queue. The app shell calls this once on mount; tests call it after their
 * spies are installed so the dispatcher binds to the mocked `subscribe`.
 *
 * Returns the unsubscribe handle.
 */
export function bindAgentDispatch(target: WsManager = ws): () => void {
  return target.subscribe((env: Envelope) => {
    switch (env.t) {
      case "agent.stream": {
        const body = env.body as AgentStream;
        const store = useChatStore.getState();
        if (body.delta) store.appendDelta(body.role, body.message_id, body.delta);
        if (body.done) store.endStream(body.role, body.message_id);
        return;
      }
      case "agent.tool_call": {
        const body = env.body as AgentToolCall;
        const store = useChatStore.getState();
        const callId = env.id;
        const existing = store.threads[body.role].toolCalls[callId];
        const patch: Partial<ToolCall> = {
          result: body.result,
          error: body.error,
        };
        if (existing) {
          store.updateToolCall(body.role, body.message_id, callId, patch);
        } else {
          store.appendToolCall(body.role, body.message_id, {
            id: callId,
            tool: body.tool,
            args: body.args,
            result: body.result,
            error: body.error,
            at: env.ts,
          });
        }
        return;
      }
      case "agent.permission.request": {
        const body = env.body as AgentPermissionRequest;
        usePermissionStore.getState().enqueue(body);
        return;
      }
      default:
        return;
    }
  });
}

/* -- Sender helpers -------------------------------------------------------- */

/**
 * Dispatch the user's decision on a pending tool-call permission prompt.
 * The hub forwards the response to the agent host's canUseTool callback.
 */
export function sendPermissionResponse(
  body: Omit<AgentPermissionResponse, "company_id"> & { company_id?: string },
): void {
  // company_id is required on the wire body; we let callers omit it only when
  // they pull it from the request (which is the common path).
  if (!body.company_id) {
    throw new Error("sendPermissionResponse: company_id required");
  }
  ws.send(
    "agent.permission.response",
    body as AgentPermissionResponse,
    { company_id: body.company_id },
  );
}
